import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Power, Settings, Timer, ToggleLeft, ToggleRight, Zap, X } from 'lucide-react';
import { dayjs } from '@/lib/dayjs';
import { showToast } from '@/lib/toast';
import { useIsMobile } from '@/hooks/useMediaQuery';
import {
  getBoardRelays,
  setRelayOverride,
  setRelayConfig,
  pulseRelay,
  type BoardRelayState,
  type RelayConfigPayload,
  type RelayMode,
} from '@/features/boards/api';

// Each mode is a real firmware autoMode — "เปิดตลอด/ปิดตลอด" are their own modes
// (not derived from override), so a "คุมด้วยปุ่ม" channel stays labelled that way
// even when its on/off buttons are pressed.
const MODE_OPTIONS: { value: RelayMode; label: string }[] = [
  { value: 'always_off', label: 'ปิดตลอด' },
  { value: 'always_on', label: 'เปิดตลอด' },
  { value: 'none', label: 'คุมด้วยปุ่ม' },
  { value: 'temp', label: 'อุณหภูมิ' },
  { value: 'schedule', label: 'ตารางเวลา (รายสัปดาห์)' },
  { value: 'calendar', label: 'ปฏิทิน (วันที่เฉพาะ)' },
  { value: 'countdown', label: 'นับถอยหลัง' },
  { value: 'cyclic', label: 'วนซ้ำ' },
  { value: 'sensor', label: 'เซ็นเซอร์' },
];
const DAY_LABELS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
const NUM_CHANNELS = 8;

const cardModeLabel = (r: BoardRelayState): string =>
  MODE_OPTIONS.find((o) => o.value === r.mode)?.label ?? r.mode;

// Short description of a channel's configured mode params (shown on the card).
const modeInfo = (r: BoardRelayState): string | null => {
  switch (r.mode) {
    case 'temp':
      return `เปิด ≥ ${r.onTemp}°C · ปิด ≤ ${r.offTemp}°C${r.curTemp != null ? ` · ตอนนี้ ${r.curTemp.toFixed(1)}°C` : ''}`;
    case 'schedule': {
      const days = r.days ? DAY_LABELS.filter((_, i) => (r.days! >> i) & 1).join(',') : 'ทุกวัน';
      return `${minToHHMM(r.onMin)}–${minToHHMM(r.offMin)} · ${days}`;
    }
    case 'cyclic':
      return `เปิด ${Math.round((r.cyOn ?? 0) / 60)} · ปิด ${Math.round((r.cyOff ?? 0) / 60)} นาที`;
    case 'calendar': {
      const f = (e?: number) => (e ? dayjs.unix(e).format('D/M/YY HH:mm') : '—');
      let st = '';
      const now = Date.now() / 1000;
      if (r.calOn && r.calOff) st = now < r.calOn ? ' · รอเริ่ม' : now < r.calOff ? ' · กำลังทำงาน' : ' · จบแล้ว';
      return `${f(r.calOn)} → ${f(r.calOff)}${st}`;
    }
    case 'countdown': {
      const d = r.dur ?? 0;
      return `กด trigger → เปิด ${d < 60 ? `${d} วิ` : `${Math.round(d / 60)} นาที`}`;
    }
    case 'sensor': {
      const met = ['กระแส (A)', 'กำลัง (W)', 'แรงดัน (V)'][r.metric ?? 0];
      const cur = r.curVal != null ? ` · ตอนนี้ ${r.curVal.toFixed(1)}` : '';
      return `${r.src ? 'PZEM' : 'KWS'} ${met} ${r.op ? '<' : '>'} ${r.thr} → ${r.actOn ? 'เปิด' : 'ตัด'}${cur}`;
    }
    default:
      return null; // manual / always on/off — mode label already says it
  }
};

const minToHHMM = (m?: number) => {
  const v = m ?? 0;
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};
const hhmmToMin = (s: string) => {
  const [h, m] = s.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
// Board/firmware store durations in SECONDS; the UI works in whole MINUTES.
const secToMin = (s?: number) => (s == null || s === 0 ? '' : String(Math.round(s / 60)));
const minToSec = (m: string) => Math.round((Number(m) || 0) * 60);

const inputStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderRadius: 8,
  border: '1px solid var(--border-color)',
  background: 'var(--bg-input)',
  color: 'var(--text)',
  fontFamily: 'inherit',
  fontSize: 13,
};

export function RelayPanel({ boardId }: { boardId: number }) {
  const isMobile = useIsMobile();
  const q = useQuery({
    queryKey: ['board-relays', boardId],
    queryFn: () => getBoardRelays(boardId),
    enabled: boardId != null,
    refetchInterval: 5000,
  });
  // 1s local ticker so countdowns (remain/auto-off) update smoothly between the
  // 5s polls instead of jumping. ageSec = how stale the last telemetry is.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const ageSec = q.data?.receivedAt ? (Date.now() - new Date(q.data.receivedAt).getTime()) / 1000 : 0;
  const reported = q.data?.relays ?? [];
  const inputs = q.data?.inputs ?? 0;
  const online = q.data != null;
  const slots: (BoardRelayState | null)[] = Array.from({ length: NUM_CHANNELS }, (_, ch) => reported.find((r) => r.ch === ch) ?? null);
  const enabledCount = reported.filter((r) => r.enabled !== false).length;
  const runningCount = reported.filter((r) => r.on).length;

  const [editing, setEditing] = useState<{ ch: number; initial: BoardRelayState | null } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Keep every button disabled through a short cooldown after a command so the
  // operator can't double-click while the board is still applying + echoing back.
  const cooldownDone = () => setTimeout(() => { void q.refetch(); setBusy(null); }, 2500);

  const doOverride = async (ch: number, mode: 'auto' | 'on' | 'off') => {
    if (busy) return;
    setBusy(`ovr-${ch}-${mode}`);
    try {
      await setRelayOverride(boardId, ch, mode);
      showToast(`ช่อง ${ch + 1}: ${mode === 'auto' ? 'อัตโนมัติ' : mode === 'on' ? 'เปิด' : 'ปิด'}`, 'info');
      cooldownDone();
    } catch (e) {
      showToast(errMsg(e, 'สั่งไม่สำเร็จ (บอร์ดออนไลน์ไหม?)'), 'error');
      setBusy(null);
    }
  };

  const doPulse = async (ch: number, minutes: number) => {
    const seconds = Math.round(minutes * 60);
    if (busy || !seconds || seconds < 1) return;
    setBusy(`pulse-${ch}`);
    try {
      await pulseRelay(boardId, ch, seconds);
      showToast(`ช่อง ${ch + 1}: เปิดชั่วคราว ${minutes} นาที`, 'info');
      cooldownDone();
    } catch (e) {
      showToast(errMsg(e, 'สั่ง pulse ไม่สำเร็จ'), 'error');
      setBusy(null);
    }
  };

  // Momentary trigger — a short ON pulse (gate/door/pump style) for web control.
  // Uses the channel's configured trigger seconds (dur), default 1s. The pulse
  // endpoint clears any latched override in the same command so it always fires.
  const doTrigger = async (ch: number) => {
    if (busy) return;
    const r = reported.find((x) => x.ch === ch);
    const sec = r?.dur && r.dur > 0 ? r.dur : 1;
    setBusy(`trig-${ch}`);
    try {
      await pulseRelay(boardId, ch, sec);
      showToast(`ช่อง ${ch + 1}: trigger ${sec} วิ`, 'info');
      cooldownDone();
    } catch (e) {
      showToast(errMsg(e, 'trigger ไม่สำเร็จ'), 'error');
      setBusy(null);
    }
  };

  return (
    <section className="card fade-in-up" style={{ marginBottom: 18, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Zap size={16} color="#22d3ee" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Relay / ระบบควบคุม (8 ช่อง)</div>
          <div style={{ fontSize: 12, color: 'var(--dim)' }}>
            {enabledCount > 0
              ? `เปิดใช้งาน ${enabledCount} ช่อง · กำลังทำงาน ${runningCount} ช่อง`
              : 'ยังไม่ได้ตั้งค่าช่องใด — กดปุ่มตั้งค่าที่การ์ดเพื่อเริ่ม'}
          </div>
        </div>
        {runningCount > 0 && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: '3px 10px',
              borderRadius: 999,
              color: '#22c55e',
              background: 'rgba(34,197,94,0.12)',
              border: '1px solid rgba(34,197,94,0.4)',
            }}
          >
            {runningCount} ON
          </span>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        {!online && (
          <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 10 }}>
            ยังไม่ได้รับสถานะจากบอร์ด (รอ telemetry — บอร์ดออนไลน์ + firmware v0.16+ ไหม?)
          </div>
        )}

        {/* Physical inputs — live states, read-only */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--dim)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            ช่อง Input (ปุ่ม/สวิตช์จริงบนบอร์ด)
            <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--dim2)', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: 999, padding: '1px 8px' }}>
              แสดงสถานะเท่านั้น
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isMobile ? 2 : 4}, minmax(0, 1fr))`, gap: 10 }}>
            {Array.from({ length: 8 }, (_, i) => {
              const pressed = (inputs >> i) & 1;
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5,
                    minHeight: 68, padding: '10px 8px', borderRadius: 9,
                    border: `1px solid ${pressed ? 'rgba(34,197,94,0.5)' : 'var(--border-color)'}`,
                    background: pressed ? 'rgba(34,197,94,0.12)' : 'var(--bg-input)',
                  }}
                >
                  {pressed
                    ? <ToggleRight size={22} color="#22c55e" />
                    : <ToggleLeft size={22} color="#64748b" />}
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: pressed ? '#22c55e' : 'var(--dim)' }}>In{i + 1}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: pressed ? '#22c55e' : 'var(--dim2)' }}>
                    {pressed ? 'กด' : 'ปล่อย'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--dim)', marginBottom: 8 }}>ช่อง Relay</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0, 1fr))', gap: 12, alignItems: 'stretch' }}>
          {slots.map((r, ch) => (
            <RelayCard
              key={ch}
              ch={ch}
              r={r}
              ageSec={ageSec}
              busy={busy}
              onOverride={doOverride}
              onPulse={doPulse}
              onTrigger={doTrigger}
              onConfig={() => setEditing({ ch, initial: r })}
            />
          ))}
        </div>
      </div>

      {editing && (
        <RelayConfigModal
          boardId={boardId}
          ch={editing.ch}
          initial={editing.initial}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setTimeout(() => void q.refetch(), 2500);
          }}
        />
      )}
    </section>
  );
}

function RelayCard({
  ch,
  r,
  ageSec,
  busy,
  onOverride,
  onPulse,
  onTrigger,
  onConfig,
}: {
  ch: number;
  r: BoardRelayState | null;
  ageSec: number;
  busy: string | null;
  onOverride: (ch: number, mode: 'auto' | 'on' | 'off') => void;
  onPulse: (ch: number, minutes: number) => void;
  onTrigger: (ch: number) => void;
  onConfig: () => void;
}) {
  const active = r != null && r.enabled !== false;      // reported + not explicitly disabled
  const configured = r != null;                          // has saved config (may be disabled)
  const info = active ? modeInfo(r!) : null;
  const countdownMin = Math.max(1, Math.round((r?.dur ?? 0) / 60) || 5);
  // Only a manual channel controlled via the web (no physical-input binding)
  // shows on/off control buttons — every other mode is status-only.
  const isManualWeb = active && r!.mode === 'none' && (!r!.inMode || r!.inMode === 0);
  // Live output state → drives the card colour + NO/NC contact label:
  //   ON  → green card, "NO" tag
  //   OFF → normal card, "NC" tag
  //   disabled → faded card (opacity)
  const on = active && !!r?.on;

  return (
    <div
      style={{
        border: `1px solid ${on ? 'rgba(34,197,94,0.5)' : 'var(--border-color)'}`,
        borderRadius: 10,
        padding: 14,
        background: on ? 'rgba(34,197,94,0.10)' : 'var(--bg-input)',
        opacity: active ? 1 : 0.62,
        // Keep every card the same height as an active one — a board with all
        // channels disabled shouldn't render a row of stubby cards.
        minHeight: 150,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: active ? 10 : 0 }}>
        <Power size={16} color={on ? '#22c55e' : '#64748b'} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r?.name || `ช่อง ${ch + 1}`}
          </div>
          <div style={{ fontSize: 11, color: 'var(--dim)' }}>
            CH{ch + 1}
            {active
              ? ` · ${cardModeLabel(r!)}${r!.interlock ? ` · interlock ${r!.interlock}` : ''}`
              : configured
                ? ` · ปิดใช้งาน (${cardModeLabel(r!)})`
                : ' · ปิดใช้งาน'}
          </div>
        </div>
        {/* Contact state — shown for EVERY channel. A relay rests at NC (coil
            de-energized) even when disabled; only an energized (ON) coil switches
            to NO. So: ON → NO (green), otherwise → NC. */}
        <span
          title={on
            ? 'Normally Open — หน้าสัมผัสต่อขณะคอยล์ทำงาน (ON)'
            : 'Normally Closed — สถานะพักปกติ (คอยล์ไม่ทำงาน)'}
          style={{
            flexShrink: 0,
            fontSize: 11, fontWeight: 800, letterSpacing: '0.04em',
            padding: '3px 10px', borderRadius: 999,
            color: on ? '#22c55e' : 'var(--dim)',
            background: on ? 'rgba(34,197,94,0.14)' : 'transparent',
            border: `1px solid ${on ? 'rgba(34,197,94,0.45)' : 'var(--border-color)'}`,
          }}
        >
          {on ? 'NO' : 'NC'}
        </span>
        <button onClick={onConfig} title="ตั้งค่า" style={{ ...inputStyle, padding: 6, cursor: 'pointer' }}>
          <Settings size={14} />
        </button>
      </div>

      {!active ? null : (
        <>
          {r!.tripped && (
            <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
              <AlertTriangle size={12} /> ตัดการทำงาน (cutoff) — กด “เปิด/อัตโนมัติ” เพื่อรีเซ็ต
            </div>
          )}
          {r!.remainSec != null && (() => {
            const s = Math.max(0, Math.round(r!.remainSec - ageSec));
            return (
              <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Timer size={12} /> เหลือ {s < 60 ? `${s} วิ` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} นาที`}
              </div>
            );
          })()}
          {r!.offInSec != null && (() => {
            const s = Math.max(0, Math.round(r!.offInSec - ageSec));
            return (
              <div style={{ fontSize: 11, color: '#f97316', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Timer size={12} /> ปิดอัตโนมัติใน {s < 60 ? `${s} วิ` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} นาที`}
              </div>
            );
          })()}
          {info && (
            <div style={{ fontSize: 11, color: 'var(--dim)', marginBottom: 8 }}>{info}</div>
          )}

          {/* Control buttons only for a web-controlled manual channel: trigger / on / off */}
          {isManualWeb && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => onTrigger(ch)}
                disabled={busy != null}
                title="เปิดแวบเดียว (1 วิ) แล้วปิด — เหมาะกับปุ่มเปิดประตู/ปั๊ม"
                style={{
                  flex: 1,
                  padding: '8px 0',
                  borderRadius: 8,
                  border: '1px solid #f59e0b',
                  background: 'rgba(245,158,11,0.1)',
                  color: '#f59e0b',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: busy != null ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                trigger
              </button>
              {(['on', 'off'] as const).map((m) => {
                const on = r!.override === m;
                return (
                  <button
                    key={m}
                    onClick={() => onOverride(ch, m)}
                    disabled={busy != null}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 8,
                      border: on ? '1px solid #22d3ee' : '1px solid var(--border-color)',
                      background: m === 'on' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                      color: m === 'on' ? '#22c55e' : '#ef4444',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: busy != null ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                      boxShadow: on ? '0 0 0 2px rgba(34,211,238,0.15)' : 'none',
                    }}
                  >
                    {m === 'on' ? 'เปิด' : 'ปิด'}
                  </button>
                );
              })}
            </div>
          )}

          {/* Countdown needs a trigger to start its timed ON */}
          {r!.mode === 'countdown' && (
            <button
              onClick={() => onPulse(ch, countdownMin)}
              disabled={busy != null}
              style={{ ...inputStyle, width: '100%', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, border: '1px solid #f59e0b', color: '#f59e0b', background: 'rgba(245,158,11,0.1)' }}
            >
              <Timer size={13} /> เริ่มนับถอยหลัง ({countdownMin} นาที)
            </button>
          )}
        </>
      )}
    </div>
  );
}

function RelayConfigModal({
  boardId,
  ch,
  initial,
  onClose,
  onSaved,
}: {
  boardId: number;
  ch: number;
  initial: BoardRelayState | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // A channel the board reports but that was never set up (disabled, no name,
  // mode none) — default such a fresh channel to "ปิดตลอด" (safe: always off).
  const isFresh = !initial || (initial.enabled === false && initial.mode === 'none' && !initial.name);
  const initialMode: RelayMode = isFresh ? 'always_off' : (initial!.mode ?? 'none');

  const [enabled, setEnabled] = useState(initial ? initial.enabled !== false : false);
  const [name, setName] = useState(initial?.name ?? '');
  const [mode, setMode] = useState<RelayMode>(initialMode);
  const [onTemp, setOnTemp] = useState(String(initial?.onTemp ?? 40));
  const [offTemp, setOffTemp] = useState(String(initial?.offTemp ?? 35));
  const [onHHMM, setOnHHMM] = useState(minToHHMM(initial?.onMin ?? 18 * 60));
  const [offHHMM, setOffHHMM] = useState(minToHHMM(initial?.offMin ?? 6 * 60));
  const [days, setDays] = useState<number>(initial?.days ?? 0);
  const [durMin, setDurMin] = useState(secToMin(initial?.dur) || '5');
  const [cdOff, setCdOff] = useState(!!initial?.cdOff);
  const [cyOnMin, setCyOnMin] = useState(secToMin(initial?.cyOn) || '5');
  const [cyOffMin, setCyOffMin] = useState(secToMin(initial?.cyOff) || '5');
  const dtLocal = (e?: number) => (e ? dayjs.unix(e).format('YYYY-MM-DDTHH:mm') : '');
  const nowLocal = dayjs().format('YYYY-MM-DDTHH:mm');
  const [calOnStr, setCalOnStr] = useState(dtLocal(initial?.calOn));
  const [calOffStr, setCalOffStr] = useState(dtLocal(initial?.calOff));
  const [metric, setMetric] = useState(initial?.metric ?? 0);
  const [src, setSrc] = useState(initial?.src ?? 0);
  const [op, setOp] = useState(initial?.op ?? 0);
  const [thr, setThr] = useState(String(initial?.thr ?? 10));
  const [actOn, setActOn] = useState<boolean>(!!initial?.actOn);
  const [maxOnMin, setMaxOnMin] = useState(secToMin(initial?.maxOnSec));
  const [interlock, setInterlock] = useState(String(initial?.interlock ?? 0));
  // physical input binding (manual mode)
  const [inMode, setInMode] = useState(initial?.inMode ?? 0);
  const [inCh, setInCh] = useState(initial?.inCh ?? ch);
  const [inTimerSec, setInTimerSec] = useState(String(initial?.inTimer ?? 5));
  const [trigSec, setTrigSec] = useState(String(initial?.dur || 1));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const toggleDay = (d: number) => setDays((prev) => prev ^ (1 << d));

  const save = async () => {
    if (enabled && mode === 'temp' && Number(onTemp) <= Number(offTemp)) {
      showToast('อุณหภูมิเปิดต้องมากกว่าอุณหภูมิปิด', 'error');
      return;
    }
    if (mode === 'calendar') {
      if (calOnStr && dayjs(calOnStr).valueOf() < Date.now() - 60_000) {
        showToast('เวลาเปิดต้องไม่ย้อนหลัง', 'error');
        return;
      }
      if (calOnStr && calOffStr && dayjs(calOnStr).valueOf() >= dayjs(calOffStr).valueOf()) {
        showToast('เวลาปิดต้องมาหลังเวลาเปิด', 'error');
        return;
      }
    }
    const cfg: RelayConfigPayload = {
      enabled,
      name: name.trim() || `ช่อง ${ch + 1}`,
      autoMode: mode,
      maxOnSec: minToSec(maxOnMin),
      interlock: Number(interlock) || 0,
    };
    if (mode === 'temp') { cfg.onTemp = Number(onTemp); cfg.offTemp = Number(offTemp); }
    if (mode === 'schedule') { cfg.onMin = hhmmToMin(onHHMM); cfg.offMin = hhmmToMin(offHHMM); cfg.days = days; }
    if (mode === 'calendar') { cfg.calOn = calOnStr ? dayjs(calOnStr).unix() : 0; cfg.calOff = calOffStr ? dayjs(calOffStr).unix() : 0; }
    if (mode === 'countdown') { cfg.dur = minToSec(durMin); cfg.cdOff = cdOff; }
    if (mode === 'cyclic') { cfg.cyOn = minToSec(cyOnMin); cfg.cyOff = minToSec(cyOffMin); }
    if (mode === 'sensor') {
      cfg.metric = metric; cfg.src = src; cfg.op = op; cfg.thr = Number(thr) || 0; cfg.actOn = !!actOn;
    }
    // Physical input binding lives on the manual ("คุมด้วยปุ่ม") mode; cleared otherwise.
    if (mode === 'none') {
      cfg.inMode = inMode;
      if (inMode === 0) cfg.dur = Number(trigSec) || 1;   // web trigger pulse seconds
      if (inMode > 0) {
        cfg.inCh = inCh;
        if (inMode === 2) cfg.inTimer = Number(inTimerSec) || 0;
      }
    } else {
      cfg.inMode = 0;
    }
    setSaving(true);
    try {
      await setRelayConfig(boardId, ch, cfg);
      // Non-manual modes drive the state themselves → reset override to auto.
      // Manual mode preserves its current on/off (don't clobber the live state).
      if (enabled && mode !== 'none') await setRelayOverride(boardId, ch, 'auto');
      showToast(`บันทึกช่อง ${ch + 1} แล้ว — รอบอร์ดยืนยัน`, 'info');
      onSaved();
    } catch (e) {
      showToast(errMsg(e, 'บันทึกไม่สำเร็จ (บอร์ดออนไลน์ไหม?)'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ flex: 1, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
            ตั้งค่า {name || initial?.name || `ช่อง ${ch + 1}`} <span style={{ color: 'var(--dim)', fontWeight: 500 }}>(CH{ch + 1})</span>
          </div>
          <button onClick={onClose} style={{ ...inputStyle, padding: 6, cursor: 'pointer' }}><X size={16} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--text)', cursor: 'pointer' }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} style={{ width: 16, height: 16 }} />
            เปิดใช้งานช่องนี้
          </label>

          <Field label="ชื่อช่อง">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={13} placeholder={`ช่อง ${ch + 1}`} style={{ ...inputStyle, width: '100%' }} />
          </Field>

          <Field label="โหมด">
            <select value={mode} onChange={(e) => setMode(e.target.value as RelayMode)} style={{ ...inputStyle, width: '100%' }}>
              {MODE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          {mode === 'temp' && (
            <div style={{ display: 'flex', gap: 12 }}>
              <Field label="เปิดเมื่อ ≥ (°C)"><input type="number" value={onTemp} onChange={(e) => setOnTemp(e.target.value)} style={{ ...inputStyle, width: 110 }} /></Field>
              <Field label="ปิดเมื่อ ≤ (°C)"><input type="number" value={offTemp} onChange={(e) => setOffTemp(e.target.value)} style={{ ...inputStyle, width: 110 }} /></Field>
            </div>
          )}

          {mode === 'schedule' && (
            <>
              <div style={{ display: 'flex', gap: 12 }}>
                <Field label="เปิดเวลา"><input type="time" value={onHHMM} onChange={(e) => setOnHHMM(e.target.value)} style={{ ...inputStyle, width: 130 }} /></Field>
                <Field label="ปิดเวลา"><input type="time" value={offHHMM} onChange={(e) => setOffHHMM(e.target.value)} style={{ ...inputStyle, width: 130 }} /></Field>
              </div>
              <Field label="วัน (ไม่เลือก = ทุกวัน)">
                <div style={{ display: 'flex', gap: 5 }}>
                  {DAY_LABELS.map((d, i) => {
                    const on = (days >> i) & 1;
                    return (
                      <button key={i} onClick={() => toggleDay(i)} style={{ ...inputStyle, padding: '6px 0', width: 38, cursor: 'pointer', background: on ? 'rgba(34,211,238,0.15)' : 'var(--bg-input)', borderColor: on ? '#22d3ee' : 'var(--border-color)', color: on ? '#22d3ee' : 'var(--dim)', fontWeight: 600 }}>{d}</button>
                    );
                  })}
                </div>
              </Field>
            </>
          )}

          {mode === 'calendar' && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Field label="เปิดเมื่อ (วันที่-เวลา)">
                <input
                  type="datetime-local"
                  value={calOnStr}
                  min={nowLocal}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCalOnStr(v);
                    // keep the OFF time after the ON time (default +1 ชม.)
                    if (v && (!calOffStr || dayjs(calOffStr).valueOf() <= dayjs(v).valueOf())) {
                      setCalOffStr(dayjs(v).add(1, 'hour').format('YYYY-MM-DDTHH:mm'));
                    }
                  }}
                  style={{ ...inputStyle, width: 220 }}
                />
              </Field>
              <Field label="ปิดเมื่อ (วันที่-เวลา)">
                <input type="datetime-local" value={calOffStr} min={calOnStr || nowLocal} onChange={(e) => setCalOffStr(e.target.value)} style={{ ...inputStyle, width: 220 }} />
              </Field>
            </div>
          )}

          {mode === 'countdown' && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Field label="นับถอยหลังแล้ว">
                <select value={cdOff ? 1 : 0} onChange={(e) => setCdOff(e.target.value === '1')} style={{ ...inputStyle, width: 210 }}>
                  <option value={0}>เปิด (กด → เปิด N แล้วดับ)</option>
                  <option value={1}>ปิด (ปกติเปิด · กด → ปิด N แล้วเปิด)</option>
                </select>
              </Field>
              <Field label="ระยะเวลา (นาที)">
                <input type="number" value={durMin} onChange={(e) => setDurMin(e.target.value)} min={1} style={{ ...inputStyle, width: 120 }} />
              </Field>
            </div>
          )}

          {mode === 'cyclic' && (
            <div style={{ display: 'flex', gap: 12 }}>
              <Field label="เปิดนาน (นาที)"><input type="number" value={cyOnMin} onChange={(e) => setCyOnMin(e.target.value)} min={1} style={{ ...inputStyle, width: 110 }} /></Field>
              <Field label="ปิดนาน (นาที)"><input type="number" value={cyOffMin} onChange={(e) => setCyOffMin(e.target.value)} min={1} style={{ ...inputStyle, width: 110 }} /></Field>
            </div>
          )}

          {mode === 'sensor' && (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Field label="มิเตอร์">
                  <select value={src} onChange={(e) => setSrc(Number(e.target.value))} style={{ ...inputStyle, width: 100 }}>
                    <option value={0}>KWS</option>
                    <option value={1}>PZEM</option>
                  </select>
                </Field>
                <Field label="ค่าที่วัด">
                  <select value={metric} onChange={(e) => setMetric(Number(e.target.value))} style={{ ...inputStyle, width: 130 }}>
                    <option value={0}>กระแส (A)</option>
                    <option value={1}>กำลัง (W)</option>
                    <option value={2}>แรงดัน (V)</option>
                  </select>
                </Field>
                <Field label="เงื่อนไข">
                  <select value={op} onChange={(e) => setOp(Number(e.target.value))} style={{ ...inputStyle, width: 80 }}>
                    <option value={0}>{'>'}</option>
                    <option value={1}>{'<'}</option>
                  </select>
                </Field>
                <Field label="ค่า threshold"><input type="number" value={thr} onChange={(e) => setThr(e.target.value)} style={{ ...inputStyle, width: 110 }} /></Field>
              </div>
              <Field label="เมื่อเข้าเงื่อนไข">
                <select value={actOn ? 1 : 0} onChange={(e) => setActOn(e.target.value === '1')} style={{ ...inputStyle, width: '100%' }}>
                  <option value={0}>ตัด/ปิด relay (cutoff — ล็อกจนกดรีเซ็ต)</option>
                  <option value={1}>เปิด relay</option>
                </select>
              </Field>
            </>
          )}

          {mode === 'none' && (
            <div style={{ borderTop: '1px dashed var(--border-color)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Field label="ควบคุมด้วยปุ่ม/สวิตช์จริงบนบอร์ด (input)">
                <select value={inMode} onChange={(e) => setInMode(Number(e.target.value))} style={{ ...inputStyle, width: '100%' }}>
                  <option value={0}>web — คุมผ่านเว็บ</option>
                  <option value={1}>latch — กดสลับ เปิด/ปิด</option>
                  <option value={2}>timed — กดแล้วเปิดตามเวลา (วินาที)</option>
                  <option value={3}>momentary — กดแล้วเปิดแวบเดียว</option>
                </select>
              </Field>
              {inMode === 0 && (
                <Field label="ระยะเวลาปุ่ม trigger (วินาที)">
                  <input type="number" value={trigSec} onChange={(e) => setTrigSec(e.target.value)} min={1} style={{ ...inputStyle, width: 150 }} />
                </Field>
              )}
              {inMode > 0 && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Field label="ใช้ Input ช่อง">
                    <select value={inCh} onChange={(e) => setInCh(Number(e.target.value))} style={{ ...inputStyle, width: 110 }}>
                      {Array.from({ length: 8 }, (_, i) => <option key={i} value={i}>Input {i + 1}</option>)}
                    </select>
                  </Field>
                  {inMode === 2 && (
                    <Field label="เปิดนาน (วินาที)"><input type="number" value={inTimerSec} onChange={(e) => setInTimerSec(e.target.value)} min={1} style={{ ...inputStyle, width: 120 }} /></Field>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ borderTop: '1px dashed var(--border-color)', paddingTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label="Auto-off timer (นาที, ว่าง=ไม่จำกัด)"><input type="number" value={maxOnMin} onChange={(e) => setMaxOnMin(e.target.value)} min={0} style={{ ...inputStyle, width: 160 }} /></Field>
            <Field label="Interlock group (0=ไม่มี)"><input type="number" value={interlock} onChange={(e) => setInterlock(e.target.value)} min={0} max={8} style={{ ...inputStyle, width: 130 }} /></Field>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={onClose} style={{ ...inputStyle, cursor: 'pointer' }}>ยกเลิก</button>
            <button
              onClick={save}
              disabled={saving}
              style={{ ...inputStyle, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, border: '1px solid #22d3ee', background: 'rgba(34,211,238,0.12)', color: '#22d3ee' }}
            >
              {saving ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ fontSize: 12, color: 'var(--dim)', display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label}
      {children}
    </label>
  );
}

function errMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}
